import Service from './Service';
import type TokenizedApi from '../TokenizedApi';
import * as schema from '../utils/schema';
import { restApiHandshake, THandshake } from './TokenizedApiModelEntities';
import { delayForSeconds, urlPath } from '../utils/utils';
import { TokenizedError } from '../utils/errors';
import { defineMessage } from '@formatjs/intl';

export class TokenizedMfaError extends TokenizedError {
  /** @internal */
  constructor(message: string) {
    super(message);
    this.name = 'MFA error';
  }
}

export class TokenizedMfaNotFoundError extends TokenizedMfaError {
  /** @internal */
  constructor() {
    super('Authorization not successful');
    this.formattedMessage = defineMessage({
      defaultMessage: 'Authorization not successful',
      description: 'Error message when mobile authorization fails unexpectedly',
      id: 'TokenizedSDK:mfa.failed',
    });
  }
}

export class TokenizedMfaCancelledError extends TokenizedMfaError {
  /** @internal */
  constructor() {
    super('Authorization request cancelled');
    this.formattedMessage = defineMessage({
      defaultMessage: 'Authorization request cancelled',
      description:
        'Error message when an operation that requires mobile authorization is cancelled',
      id: 'TokenizedSDK:mfa.cancelled',
    });
  }
}

export class TokenizedMfaDeniedError extends TokenizedMfaError {
  /** @internal */
  constructor() {
    super('Mobile authorization cancelled');
    this.formattedMessage = defineMessage({
      defaultMessage: 'Mobile authorization cancelled',
      description:
        'Error message when an authorization request is cancelled in the mobile app',
      id: 'TokenizedSDK:mfa.cancelled.mobile',
    });
  }
}

export class MFARequest {
  /** @internal */
  #handshakeId: string;
  /** @internal */
  #mfa: MFA;

  confirmationCode: string;
  activityId?: string;

  /** @internal */
  constructor(
    mfa: MFA,
    handshakeId: string,
    confirmationCode: string,
    activityId?: string,
  ) {
    this.#handshakeId = handshakeId;
    this.#mfa = mfa;
    this.confirmationCode = confirmationCode;
    this.activityId = activityId;
  }

  /** @internal */
  static async make(mfa: MFA, handshakeId: string, activityId?: string) {
    const confirmationCode = await handshakeConfirmationCode(handshakeId);
    return new MFARequest(mfa, handshakeId, confirmationCode, activityId);
  }

  cancel() {
    this.#mfa.cancelMfa(this.#handshakeId);
  }
}

/** @category SDK Services */
export default class MFA extends Service {
  /** @internal */
  mfaAbort?: AbortController;

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
  }

  async waitForMfa(
    handshakeId: string,
    setMfaRequest?: (mfaRequest: MFARequest | null) => void,
    activityId?: string,
  ) {
    if (!handshakeId?.length || typeof handshakeId !== 'string') {
      throw new TypeError('waitForMfa: activityId must be a non-empty string');
    }

    setMfaRequest?.(await MFARequest.make(this, handshakeId, activityId));

    try {
      this.mfaAbort = new AbortController();
      while (true) {
        const response = await this.tokenizedApi.endpoints.get(
          urlPath('auth', 'handshakes', 'poll'),
          {
            params: {
              filter: {
                id: handshakeId,
              },
            },
          },
        );

        const handshake = schema.normalizeResponse(
          'Retrieve handshake',
          response?.data?.data,
          restApiHandshake,
        ).normalizedValue;

        if (!handshake?.status) {
          // The handshakes/poll endpoint automatically filters out
          // expired handshakes in its SQL query, so when a handshake expires
          // we get a 204 response, rather than an error or an expired status.
          throw new TokenizedMfaNotFoundError().withFormatting(this);
        }

        if (handshake.status === 'approved') {
          return;
        }

        // cancelMfa() might have been called while we were fetching
        // the handshake status
        const wasCancelled = this.mfaAbort.signal.aborted;
        if (!wasCancelled && handshake.status !== 'pending') {
          throw new TokenizedMfaDeniedError().withFormatting(this);
        }

        try {
          if (wasCancelled) {
            throw this.mfaAbort.signal.reason;
          }
          await delayForSeconds(1, this.mfaAbort.signal);
        } catch (handshakeCancelProcess) {
          let error: Error = new TokenizedMfaCancelledError().withFormatting(
            this,
          );
          if (handshakeCancelProcess) {
            const cancelError = await (handshakeCancelProcess as Promise<
              Error | undefined
            >);
            if (cancelError) {
              error = cancelError;
            }
          }
          throw error;
        }
      }
    } finally {
      setMfaRequest?.(null);
      await this.clearupExpiredHandshakes();
    }
  }

  async clearupExpiredHandshakes() {
    try {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('auth', 'handshakes', 'expired'),
      );

      const handshakes = schema.normalizeResponse(
        'Retrieve handshake',
        response?.data?.data,
        schema.arrayOf(restApiHandshake),
      ).normalizedValue;

      if (handshakes) {
        for (const handshake of handshakes) {
          await this.endHandshake(handshake, 'expired');
        }
      }
    } catch (error) {
      console.log(
        'Ignoring error: Unable to clear up expired handshakes',
        error,
      );
    }
  }

  async endHandshake(handshake: THandshake, status: string) {
    await this.tokenizedApi.endpoints.put(urlPath('auth', 'handshakes'), {
      handshake_id: handshake.id,
      status: status,
    });
  }

  async cancelMfa(mfa: string) {
    const handshakeCancelProcess = (async () => {
      try {
        if (mfa) {
          const response = await this.tokenizedApi.endpoints.get(
            urlPath('auth', 'handshakes', mfa),
          );

          const handshake = schema.normalizeResponse(
            'Retrieve handshake',
            response?.data?.data,
            restApiHandshake,
          ).normalizedValue;

          if (handshake) {
            await this.endHandshake(handshake, 'cancelled');
          }
        } else {
          await this.tokenizedApi.endpoints.delete(
            urlPath('auth', 'handshakes'),
          );
        }
      } catch (error) {
        return error;
      }
    })();
    this.mfaAbort?.abort(handshakeCancelProcess);
  }
}

export async function handshakeConfirmationCode(handshakeId: string) {
  const hash = await window.crypto.subtle.digest(
    'SHA-256',
    Buffer.from(handshakeId),
  );
  return Buffer.from(hash)
    .readUInt32LE()
    .toString(36)
    .padStart(6, '0')
    .slice(0, 6)
    .replace(/(...)(...)/, '$1-$2')
    .toUpperCase();
}
